import { useContext, useState, useEffect } from "react";
import { UserContext } from "./UserContext";
import { format, formatDistanceToNow, isBefore, subYears } from "date-fns";
import config from "./config";

export default function Comment({
  key,
  postId,
  author,
  userId,
  text,
  date,
  commentId,
  isActionEnabled = true,
  replies = [],
}) {
  const [replying, setReplying] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [guestUsername, setGuestUsername] = useState("");
  const [fetchedReplies, setFetchedReplies] = useState([]);
  const [showReplies, setShowReplies] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const { userInfo } = useContext(UserContext);

  const username = userInfo?.username;

  // Load replies from sessionStorage on component mount
  useEffect(() => {
    const storedReplies = sessionStorage.getItem(`replies-${commentId}`);
    if (storedReplies) {
      setFetchedReplies(JSON.parse(storedReplies));
    }
  }, [commentId]);

  async function handleReplySubmit(event) {
    event.preventDefault();

    const replyData = {
      text: replyContent,
      guestUsername: !username ? guestUsername : undefined,
      postId: postId,
    };

    try {
      const response = await fetch(`${config.apiUrl}/post/${commentId}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(replyData),
      });

      if (response.ok) {
        const newReply = await response.json();

        // Clear the reply input after success
        setReplyContent("");
        setReplying(false);
        setShowReplies(true);

        // Update sessionStorage and fetchedReplies
        const updatedReplies = [...fetchedReplies, newReply];
        sessionStorage.setItem(
          `replies-${commentId}`,
          JSON.stringify(updatedReplies),
        );
        setFetchedReplies(updatedReplies);
      } else {
        const errorData = await response.json();
        alert(
          errorData.error || "An error occurred while submitting your comment",
        );
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      alert("An unexpected error occurred");
    }
  }

  // Function to fetch replies from the backend
  async function fetchReplies() {
    if (loadingReplies) return;
    setLoadingReplies(true);

    try {
      const response = await fetch(
        `${config.apiUrl}/post/${commentId}/replies`,
      );
      if (response.ok) {
        const data = await response.json();
        setFetchedReplies(data);

        // Save fetched replies to sessionStorage
        sessionStorage.setItem(`replies-${commentId}`, JSON.stringify(data));
      }
    } catch (error) {
      alert("Error occurred fetching replies");
    } finally {
      setLoadingReplies(false);
    }
  }

  // Handle showing and hiding replies
  const handleToggleReplies = () => {
    if (showReplies) {
      setShowReplies(false); // Hide replies
    } else {
      if (fetchedReplies.length === 0) {
        // If replies haven't been fetched, fetch them first
        fetchReplies();
      }
      setShowReplies(true); // Show replies
    }
  };

  const formatDate = (date) => {
    const commentDate = new Date(date);
    const oneYearAgo = subYears(new Date(), 1);

    if (isBefore(commentDate, oneYearAgo)) {
      // If older than one year, display the full date
      return format(commentDate, "dd MMM yyyy");
    } else {
      // Display relative time (like "1 minute ago")
      return formatDistanceToNow(commentDate, { addSuffix: true });
    }
  };

  return (
    <div className="comment">
      <div className="comment-body">
        <div className="comment-header">
          <div className="author-info">
            <img
              src={
                userId
                  ? `${config.apiUrl}/${userId.picturePath}`
                  : `${config.apiUrl}/uploads/users/fallback.jpg`
              }
              alt="author-profile"
              className="author-info"
            />

            {isActionEnabled && userId ? (
              <a href={`/profile/${userId._id}`} className="author-link">
                <h3>{author}</h3>
              </a>
            ) : (
              <h3>{author}</h3>
            )}
          </div>
          <time>{formatDate(date)}</time>
        </div>

        <div className="comment-text">
          <p>{text}</p>
        </div>
        {isActionEnabled && (
          <div className="action-row">
            {replies.length > 0 && ( // Conditionally render the button
              <button
                className="button show-replies-button"
                onClick={handleToggleReplies}
              >
                {showReplies ? <span>-</span> : <span>+</span>}{" "}
              </button>
            )}
            <button
              className="button reply-button"
              onClick={() => setReplying(!replying)}
            >
              <i className="fa fa-reply"></i> Reply
            </button>
          </div>
        )}

        {isActionEnabled && replying && (
          <form className="reply-form" onSubmit={handleReplySubmit}>
            <textarea
              placeholder="Your reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              required
              className="reply-textarea"
            />
            {!username && (
              <input
                type="text"
                placeholder="Your Nickname"
                value={guestUsername}
                onChange={(e) => setGuestUsername(e.target.value)}
                className="guest-nickname-input"
              />
            )}
            <button type="submit" className="submit-reply-button">
              Submit Reply
            </button>
          </form>
        )}

        {isActionEnabled && showReplies && fetchedReplies.length > 0 && (
          <div className="replies">
            {fetchedReplies.map((reply) => (
              <Comment
                className="reply"
                key={reply._id}
                postId={postId}
                author={reply.author}
                userId={reply.userId}
                text={reply.text}
                date={reply.createdAt}
                commentId={reply._id}
                replies={reply.replies}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
